import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGetBooks } from '../../api';

const STORAGE_KEY = 'bookscape_recent_searches';

export const useGlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const requestSeq = useRef(0);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setRecentSearches(parsed);
      } catch {
        /* ignore corrupt storage */
      }
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearchError('');
      setLoading(false);
      setActiveIndex(-1);
      return undefined;
    }

    setLoading(true);
    setSearchError('');
    const mySeq = ++requestSeq.current;

    const timer = setTimeout(() => {
      apiGetBooks(`search=${encodeURIComponent(term)}&limit=8`)
        .then((data) => {
          if (mySeq !== requestSeq.current) return;
          setResults(Array.isArray(data?.books) ? data.books : []);
        })
        .catch((err) => {
          if (mySeq !== requestSeq.current) return;
          setResults([]);
          setSearchError(err?.message || 'Search failed');
        })
        .finally(() => {
          if (mySeq !== requestSeq.current) return;
          setLoading(false);
          setActiveIndex(-1);
        });
    }, 250);

    return () => clearTimeout(timer);
  }, [query]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveRecentSearch = (term) => {
    const trimmed = (term || '').trim();
    if (!trimmed) return;
    const updated = [trimmed, ...recentSearches.filter((s) => s !== trimmed)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const goToShop = (term) => {
    const trimmed = (term || '').trim();
    if (!trimmed) return;
    saveRecentSearch(trimmed);
    navigate(`/shop?search=${encodeURIComponent(trimmed).replace(/%20/g, '+')}`);
    setIsOpen(false);
    setQuery('');
  };

  const handleSuggestionClick = (term) => {
    const trimmed = (term || '').trim();
    if (!trimmed) return;
    setQuery(trimmed);
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    goToShop(query);
  };

  const handleBookClick = (book) => {
    goToShop(book.title || query);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleKeyDown = (e) => {
    const totalItems = results.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && results[activeIndex]) {
        e.preventDefault();
        handleBookClick(results[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSearchError('');
    inputRef.current?.focus();
  };

  return {
    query,
    setQuery,
    results,
    loading,
    searchError,
    isOpen,
    setIsOpen,
    activeIndex,
    recentSearches,
    inputRef,
    wrapperRef,
    handleSubmit,
    handleBookClick,
    handleSuggestionClick,
    handleKeyDown,
    clearRecentSearches,
    clearSearch,
  };
};
